class CardProductForm extends HTMLElement {
  constructor() {
    super();
    this.variantsBlacklist = [40493254672436];

    this.specialVariantsKey = this.dataset['specialVariantsKey'];
    this.specialVariantsConfig = [
      {
        productHandle: 'zestaw-more-collagen-i-wybrany-suplement',
        blockedOptionName: 'Wybierz suplement',
        blockedOptionId: 'gid://shopify/ProductOption/18578048975183',
        getBlockedOptionSelectValue(key) {
          switch (key) {
            case 'WITAMINA_D3':
              return 'Witamina D3 Inner SUN';
            case 'OMEGA_3':
              return 'Omega-3 PRO Pure BALANCE';
            case 'MAGNEZ_SIMAG':
              return 'Magnez 100% Simag™ 55 Calm SUPPORT';
            case 'B_COMPLEX':
              return 'B-complex FORTE Metabolism BOOST';
            case 'MASLAN_SODU':
              return 'Maślan sodu FORTE HEALTHY Gut';
            default:
              return null;
          }
        },
        getTitle(title, key) {
          const variant = this.getBlockedOptionSelectValue(key);
          return title.replace('wybrany suplement', variant);
        },
      },
    ];

    this.data = JSON.parse(this.querySelector('.card-form-dataset').innerText) || {};
    this.specialVariantRule = this.getSpecialVariantRule();
    this.variants = (Array.isArray(this.data.variants) ? this.data.variants : []).filter(
      this.filterVariants.bind(this)
    );
    this.selectedVariant = this.variants[0] || null;
    this.cardTitle = this.getCardTitleElement();
    this.defaultCardTitle = this.cardTitle?.textContent.trim() || this.data?.title || '';
    this.cardImage = this.getCardImageElement();
    this.defaultCardImage = this.getImageAttributes(this.cardImage);
    this.subscriptionDisabled = JSON.parse(this.dataset['subscriptionDisabled'] ?? false);

    this.isSellingPlanEnabled = null;
    this.sellingPlanContainer = null;
    this.buttonContainer = null;
    this.root = null;
    this.submitEventData = this.dataset['submitEventData'] ? JSON.parse(this.dataset['submitEventData']) : null;
    this.sourceWidgetId = this.dataset['sourceWidgetId'];

    this.renderRoot();
    this.renderVariantSelect();
    this.renderSellingPlanSelect();
    this.renderButton();
    this.renderSellingPlanCheckbox();

    this.root.addEventListener('submit', this.formSubmitHandler.bind(this));

    if (this.selectedVariant) {
      this.onVariantChange({
        target: { value: this.selectedVariant.id },
      });
    }
  }

  normalizeId(value) {
    if (value == null) return '';
    return String(value).replace(/^gid:\/\/shopify\/SellingPlan(Group)?\//, '');
  }

  getSellingPlanGroups() {
    return Array.isArray(this.data?.selling_plan_groups) ? this.data.selling_plan_groups : [];
  }

  getRecurringSellingPlans(group = null) {
    const plans = Array.isArray(group?.selling_plans) ? group.selling_plans : [];

    return plans.filter((plan) => {
      if (plan?.recurring_deliveries === true) return true;
      if (plan?.recurring_deliveries === false) return false;
      return typeof plan?.name === 'string' ? plan.name === plan.name.trim() : false;
    });
  }

  getPlanDiscountPercent(plan = null, group = null) {
    const planPercent = Number(plan?.price_adjustments?.[0]?.value);
    if (!Number.isNaN(planPercent) && planPercent > 0) return planPercent;

    const groupDiscountAmount = Number(group?.discount_amount);
    if (!Number.isNaN(groupDiscountAmount) && groupDiscountAmount > 0) return groupDiscountAmount;

    const groupNameMatch = typeof group?.name === 'string' ? group.name.match(/(\d+)\s*%/) : null;
    return groupNameMatch ? Number(groupNameMatch[1]) : 0;
  }

  getPreferredSellingPlanGroup() {
    const groups = this.getSellingPlanGroups();

    return (
      groups.reduce((bestGroup, group) => {
        const recurringPlans = this.getRecurringSellingPlans(group);
        if (recurringPlans.length === 0) return bestGroup;

        const groupPercent = Math.max(...recurringPlans.map((plan) => this.getPlanDiscountPercent(plan, group)), 0);
        const bestPercent = bestGroup ? bestGroup.discountPercent : -1;

        if (groupPercent > bestPercent) {
          return { group, discountPercent: groupPercent };
        }

        return bestGroup;
      }, null)?.group || null
    );
  }

  getDefaultSellingPlan() {
    const recurringPlans = this.getRecurringSellingPlans(this.getPreferredSellingPlanGroup());
    return recurringPlans.find((plan) => /\b2\b/.test(plan.name)) || recurringPlans[0] || null;
  }

  getSelectedSellingPlanId() {
    const select = this.sellingPlanContainer?.querySelector('select[name="selling-plan-select"]');
    return select?.value || this.getDefaultSellingPlan()?.id || null;
  }

  getVariantSubscriptionAllocation(variant = this.selectedVariant, sellingPlanId = this.getSelectedSellingPlanId()) {
    if (!variant || !Array.isArray(variant.selling_plan_allocations)) return null;

    const preferredGroup = this.getPreferredSellingPlanGroup();
    const preferredGroupId = this.normalizeId(preferredGroup?.id);
    const normalizedSellingPlanId = this.normalizeId(sellingPlanId);

    if (normalizedSellingPlanId) {
      const matchedByPlanId = variant.selling_plan_allocations.find(
        (allocation) => this.normalizeId(allocation?.selling_plan?.id) === normalizedSellingPlanId
      );
      if (matchedByPlanId) return matchedByPlanId;
    }

    if (preferredGroupId) {
      const matchedByGroupId = variant.selling_plan_allocations.find(
        (allocation) => this.normalizeId(allocation?.selling_plan_group_id) === preferredGroupId
      );
      if (matchedByGroupId) return matchedByGroupId;
    }

    return (
      variant.selling_plan_allocations.find((allocation) => allocation?.selling_plan?.recurring_deliveries) ||
      variant.selling_plan_allocations[0] ||
      null
    );
  }

  getVariantSubscriptionPrice(variant = this.selectedVariant) {
    const allocation = this.getVariantSubscriptionAllocation(variant);
    return allocation?.per_delivery_price ?? allocation?.price ?? null;
  }

  updateDisplayedPrice() {
    const priceContainer = this.buttonContainer?.querySelector('.variant-price');
    if (!priceContainer || !this.selectedVariant) return;

    const displayPrice =
      this.isSellingPlanEnabled && this.getVariantSubscriptionPrice(this.selectedVariant) != null
        ? this.getVariantSubscriptionPrice(this.selectedVariant)
        : this.selectedVariant.price;

    priceContainer.textContent = this.formatPrice(displayPrice);
  }

  renderRoot() {
    const form = document.createElement('form');
    form.className = 'card-product-form flex flex-col gap-2';
    this.root = form;
    this.appendChild(form);
  }

  renderVariantSelect() {
    const container = document.createElement('div');
    container.className = 'card-form-variant-select';
    container.innerHTML = /* HTML */ `
      <select class="card-product-select rounded-sm ${this.variants.length < 2 ? 'hidden' : ''}" name="variant-select">
        ${this.variants.map((v) => `<option value="${v.id}">${this.getVariantSelectLabel(v)}</option>`)}
      </select>
    `;

    container.querySelector('select').addEventListener('change', this.onVariantChange.bind(this));

    this.root.appendChild(container);
  }

  renderSellingPlanSelect() {
    const plans = this.getRecurringSellingPlans(this.getPreferredSellingPlanGroup());
    if (plans.length < 2 || this.subscriptionDisabled) return;

    const defaultSellingPlan = this.getDefaultSellingPlan();

    const container = document.createElement('div');
    container.className = 'card-form-selling-plans-select hidden';
    container.innerHTML = /* HTML */ `
      <select class="card-product-select" name="selling-plan-select">
        ${plans.map(
          (p) => `<option value="${p.id}" ${p.id === defaultSellingPlan?.id ? 'selected' : ''}>${p.name}</option>`
        )}
      </select>
    `;

    container.querySelector('select').addEventListener('change', () => {
      this.updateDisplayedPrice();
    });

    this.sellingPlanContainer = container;
    this.root.appendChild(container);
  }

  renderSellingPlanCheckbox() {
    const preferredGroup = this.getPreferredSellingPlanGroup();
    const plans = this.getRecurringSellingPlans(preferredGroup);
    if (plans.length < 2 || this.subscriptionDisabled) return;

    const discountPercent = this.getPlanDiscountPercent(plans[0], preferredGroup);

    const container = document.createElement('div');
    container.className = 'card-form-checkbox card-product-checkbox';
    container.innerHTML = /* HTML */ `
      <label class="form-check-label tracking-tight lg:tracking-normal font-medium">
        <input type="checkbox" name="selling_plan_enabled" />
        Zamień na subskrypcję i oszczędź ${discountPercent}%
      </label>
    `;

    const checkbox = container.querySelector('input[name="selling_plan_enabled"]');
    checkbox.addEventListener('change', (e) => {
      this.onSellingPlanVisibilityChange(e.target.checked);
    });

    this.root.appendChild(container);
  }

  renderButton() {
    const container = document.createElement('div');
    container.className = 'card-form-button-wrapper';
    container.innerHTML = /* HTML */ `
      <button
        class="card-form-button w-full btn btn--secondary button font-semibold h-[30px] shrink-0 px-5 py-2.5 justify-center flex-1 whitespace-nowrap tab:flex-none gap-3 rounded relative hover:bg-black hover:text-white"
      >
        Dodaj <span class="variant-price"></span>
      </button>
    `;

    this.buttonContainer = container;
    this.root.appendChild(container);
  }

  async formSubmitHandler(e) {
    e.preventDefault();
    const formData = new FormData(e.target);

    const button = this.buttonContainer.querySelector('.card-form-button');

    const variantId = formData.get('variant-select');
    const sellingPlanId = formData.get('selling-plan-select');
    const sellingPlanEnabled = formData.get('selling_plan_enabled');

    button.removeAttribute('clicked');
    button.setAttribute('loading', true);
    button.setAttribute('disabled', true);

    console.log('this.submitEventData', this.submitEventData);

    if (this.submitEventData) {
      await dataLayerPromise(this.submitEventData);
    }

    if (sellingPlanEnabled) {
      this.addVariantToCart(variantId, sellingPlanId).then(() => {
        button.removeAttribute('loading');
        button.setAttribute('clicked', true);
        button.removeAttribute('disabled');
        setTimeout(() => {
          button.removeAttribute('clicked');
        }, 3000);
      });
    } else {
      this.addVariantToCart(variantId).then(() => {
        button.removeAttribute('loading');
        button.setAttribute('clicked', true);
        button.removeAttribute('disabled');
        setTimeout(() => {
          button.removeAttribute('clicked');
        }, 3000);
      });
    }
  }

  onSellingPlanVisibilityChange(isVisible) {
    this.isSellingPlanEnabled = isVisible;
    this.sellingPlanContainer?.classList[isVisible ? 'remove' : 'add']('hidden');
    this.updateDisplayedPrice();
  }

  updateOtherFormInput(variantId) {
    const key = this.dataset['variantInputKey'];
    if (key) {
      const input = document.querySelector(`input[${key}="${this.data.id}"]`);
      if (input && variantId) input.value = variantId;
    }
  }

  onVariantChange(e) {
    const selectedVariant = this.variants.find((v) => String(v.id) === String(e.target.value));
    if (!selectedVariant) return;

    this.selectedVariant = selectedVariant;
    this.updateDisplayedPrice();
    this.updateOtherFormInput(selectedVariant.id);
    this.updateCardTitle();
    this.updateCardImage(selectedVariant);
  }

  async addVariantToCart(variantId, sellingPlanId) {
    const item = {
      quantity: 1,
      id: variantId,
      ...(this.sourceWidgetId && {
        properties: {
          _source: 'YK',
          _yk_source: 'YK',
          _widget_id: this.sourceWidgetId,
        },
      }),
      ...(sellingPlanId && { selling_plan: sellingPlanId }),
    };

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: [item] }),
      });
      dataLayerPromise({
        event: 'add_to_cart_custom',
        widget_type: 'YK',
      });

      if (!response.ok) throw new Error(`Błąd: ${response.status}`);
    } catch (err) {
      console.error('Błąd przy dodawaniu produktu do koszyka:', err);
    }
  }

  filterVariants(v) {
    if (this.variantsBlacklist.includes(v.id)) return false;
    if (!this.specialVariantRule) return true;

    return this.isSameOptionValue(
      this.getVariantOptionValue(v, this.specialVariantRule.blockedOptionIndex),
      this.specialVariantRule.blockedOptionValue
    );
  }

  getSpecialVariantRule() {
    const productHandle = this.data?.handle;
    const config = this.specialVariantsConfig.find((item) => item.productHandle === productHandle);
    if (!config) return null;

    const blockedOptionValue = config.getBlockedOptionSelectValue?.(this.specialVariantsKey);
    if (!blockedOptionValue) return null;

    const blockedOptionIndex = this.getBlockedOptionIndex(config);
    if (blockedOptionIndex < 0) return null;

    return {
      ...config,
      blockedOptionIndex,
      blockedOptionValue,
    };
  }

  getBlockedOptionIndex(config) {
    const optionId = config.blockedOptionId || config.optionId;
    const optionName = config.blockedOptionName || config.optionName;
    const options = this.getProductOptions();
    const normalizedOptionId = this.normalizeOptionId(optionId);

    if (normalizedOptionId) {
      const optionIndex = options.findIndex((option) => this.normalizeOptionId(option.id) === normalizedOptionId);
      if (optionIndex >= 0) return optionIndex;
    }

    if (!optionName) return -1;

    return options.findIndex((option) => this.isSameOptionName(option.name, optionName));
  }

  getProductOptions() {
    const optionsWithValues = Array.isArray(this.data?.options_with_values) ? this.data.options_with_values : [];
    if (optionsWithValues.length > 0) {
      return optionsWithValues.map((option, index) => this.normalizeProductOption(option, index));
    }

    const options = Array.isArray(this.data?.options) ? this.data.options : [];
    return options.map((option, index) => this.normalizeProductOption(option, index));
  }

  normalizeProductOption(option, index) {
    if (typeof option === 'string') {
      return {
        id: null,
        name: option,
        position: index + 1,
      };
    }

    return {
      id: option?.id || option?.admin_graphql_api_id || option?.gid || null,
      name: option?.name || '',
      position: option?.position || index + 1,
    };
  }

  normalizeOptionId(value) {
    if (value == null) return '';
    return String(value).replace(/^gid:\/\/shopify\/ProductOption\//, '');
  }

  normalizeOptionValue(value) {
    return String(value ?? '').trim();
  }

  isSameOptionName(currentName, expectedName) {
    return (
      this.normalizeOptionValue(currentName).toLowerCase() === this.normalizeOptionValue(expectedName).toLowerCase()
    );
  }

  isSameOptionValue(currentValue, expectedValue) {
    return this.normalizeOptionValue(currentValue) === this.normalizeOptionValue(expectedValue);
  }

  getVariantOptionValue(variant, optionIndex) {
    if (Array.isArray(variant?.options)) return variant.options[optionIndex] || null;
    return variant?.[`option${optionIndex + 1}`] || null;
  }

  getVariantOptionValues(variant) {
    const optionsCount = this.getProductOptions().length;
    const variantOptions = Array.isArray(variant?.options) ? variant.options : [];
    const valuesCount = Math.max(optionsCount, variantOptions.length);

    return Array.from({ length: valuesCount }, (_, index) => this.getVariantOptionValue(variant, index));
  }

  getVariantSelectLabel(variant) {
    if (!this.specialVariantRule) return variant.public_title;

    const visibleOptions = this.getVariantOptionValues(variant).filter((value, index) => {
      return index !== this.specialVariantRule.blockedOptionIndex && this.normalizeOptionValue(value);
    });

    return visibleOptions.join(' / ') || variant.public_title;
  }

  getCardImageElement() {
    const card = this.closest('.card-wrapper--product') || this.closest('.card-wrapper');
    return card?.querySelector('.card__media .card-media.img-primary') || null;
  }

  getCardTitleElement() {
    const card = this.closest('.card-wrapper--product') || this.closest('.card-wrapper');
    return card?.querySelector('.card__heading') || null;
  }

  getSpecialVariantTitle() {
    if (!this.specialVariantRule?.getTitle || !this.defaultCardTitle) return null;

    return this.specialVariantRule.getTitle(this.defaultCardTitle, this.specialVariantsKey) || null;
  }

  updateCardTitle() {
    const title = this.cardTitle || this.getCardTitleElement();
    if (!title) return;

    title.textContent = this.getSpecialVariantTitle() || this.defaultCardTitle;
  }

  getImageAttributes(image) {
    if (!image) return null;

    return {
      src: image.getAttribute('src'),
      srcset: image.getAttribute('srcset'),
      alt: image.getAttribute('alt'),
      width: image.getAttribute('width'),
      height: image.getAttribute('height'),
    };
  }

  setImageAttribute(image, name, value) {
    if (value == null || value === '') {
      image.removeAttribute(name);
      return;
    }

    image.setAttribute(name, value);
  }

  getVariantImage(variant) {
    const image = variant?.featured_image || variant?.featured_media?.preview_image || variant?.featured_media || null;

    if (!image) return null;

    if (typeof image === 'string') {
      return {
        src: image,
      };
    }

    return {
      ...image,
      src: image.src || image.url || image.preview_image?.src || image.preview_image?.url || null,
    };
  }

  getShopifyImageUrl(src, width) {
    if (!src || !width) return src;

    try {
      const isProtocolRelative = src.startsWith('//');
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);

      if (isProtocolRelative) {
        return `//${url.host}${url.pathname}${url.search}${url.hash}`;
      }

      return url.toString();
    } catch (err) {
      return src;
    }
  }

  getVariantImageSrcset(image) {
    const imageWidth = Number(image?.width);
    const srcsetConfig = [
      { minWidth: 165, imageWidth: 165, descriptor: 165 },
      { minWidth: 360, imageWidth: 360, descriptor: 360 },
      { minWidth: 533, imageWidth: 500, descriptor: 533 },
      { minWidth: 720, imageWidth: 500, descriptor: 720 },
      { minWidth: 940, imageWidth: 500, descriptor: 940 },
      { minWidth: 1066, imageWidth: 500, descriptor: 1066 },
    ];

    return srcsetConfig
      .filter(({ minWidth }) => !imageWidth || imageWidth >= minWidth)
      .map(({ imageWidth, descriptor }) => `${this.getShopifyImageUrl(image.src, imageWidth)} ${descriptor}w`)
      .join(', ');
  }

  restoreDefaultCardImage(image) {
    if (!image || !this.defaultCardImage) return;

    Object.entries(this.defaultCardImage).forEach(([name, value]) => {
      this.setImageAttribute(image, name, value);
    });
  }

  updateCardImage(variant) {
    const image = this.cardImage || this.getCardImageElement();
    if (!image) return;

    const variantImage = this.getVariantImage(variant);
    if (!variantImage?.src) {
      this.restoreDefaultCardImage(image);
      return;
    }

    image.setAttribute('src', this.getShopifyImageUrl(variantImage.src, 500));
    this.setImageAttribute(image, 'srcset', this.getVariantImageSrcset(variantImage));

    this.setImageAttribute(image, 'alt', variantImage.alt || this.data?.title || variant?.title);
    this.setImageAttribute(image, 'width', variantImage.width || this.defaultCardImage?.width);
    this.setImageAttribute(image, 'height', variantImage.height || this.defaultCardImage?.height);
  }

  formatPrice(price) {
    price = (Number(price) / 100).toFixed(2);
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN',
    }).format(price);
  }
}

customElements.define('card-product-form', CardProductForm);
